import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AppConfig, ConfigContextType, SettingsValueMap, AppConfigData, StaticConfigData, DynamicConfigData } from '@/types/config';
import { configService } from '@/services/ConfigService';

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig>(configService.getConfig());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const loadedConfig = await configService.loadConfig();
        setConfig(loadedConfig);
        
        // Check for existing session storage data and map it accordingly
        const settingsWithStorage = getSettingsValueMapWithStorage();
        
        // Save the mapped settings back to storage to ensure consistency
        configService.saveSettingsToStorage();
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load configuration');
        console.error('Config loading error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);

  const updateConfig = (updates: Partial<AppConfig>) => {
    configService.updateConfig(updates);
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const reloadConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const loadedConfig = await configService.loadConfig();
      setConfig(loadedConfig);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reload configuration');
      console.error('Config reload error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get app configuration (metadata and pages)
   */
  const getAppConfig = (): AppConfigData => ({
    metadata: config.metadata,
    pages: config.pages
  });

  const getStaticConfig = (): StaticConfigData => {
    return config.layout || {};
  };

  const getDynamicConfig = (): DynamicConfigData => {
    const { metadata, pages, layout, ...dynamicConfig } = config;
    return dynamicConfig;
  };

  const getSettingsValueMap = (): SettingsValueMap => {
    return configService.generateSettingsValueMap();
  };

  const getSettingsValueMapWithStorage = (): SettingsValueMap => {
    return configService.checkAndMapStorageData();
  };

  const mapStorageToConfig = (storedData: SettingsValueMap): Record<string, unknown> => {
    return configService.mapStorageToConfig(storedData);
  };

  const saveSettingsToStorage = (): void => {
    configService.saveSettingsToStorage();
  };

  const loadSettingsFromStorage = (): SettingsValueMap => {
    return configService.loadSettingsFromStorage();
  };

  const clearSettingsFromStorage = (): void => {
    configService.clearSettingsFromStorage();
  };

  const getVisibleTabs = (): string[] => {
    return configService.getVisibleTabs();
  };

  const getTabSettings = (tabName: string): Record<string, unknown> => {
    return configService.getTabSettings(tabName);
  };

  const getTabLabels = (): Array<{ name: string; label: string }> => {
    return configService.getTabLabels();
  };

  const getTabLabel = (tabName: string): string => {
    return configService.getTabLabel(tabName);
  };

  /**
   * Get page configuration for a specific route
   */
  const getPageConfig = (pageRoute: string) => configService.getPageConfig(pageRoute);

  /**
   * Get all available page routes
   */
  const getPageRoutes = () => configService.getPageRoutes();

  /**
   * Check if a page route exists
   */
  const hasPageRoute = (pageRoute: string) => configService.hasPageRoute(pageRoute);

  /**
   * Get page metadata (name, subTitle, etc.)
   */
  const getPageMetadata = (pageRoute: string) => configService.getPageMetadata(pageRoute);

  /**
   * Get page agent label
   */
  const getPageAgentLabel = (pageRoute: string) => configService.getPageAgentLabel(pageRoute);

  /**
   * Get page info data (header, subheader, list)
   */
  const getPageInfo = (pageRoute: string) => configService.getPageInfo(pageRoute);

  const value: ConfigContextType = {
    config,
    isLoading,
    error,
    updateConfig,
    reloadConfig,
    getAppConfig,
    getStaticConfig,
    getDynamicConfig,
    getSettingsValueMap,
    getSettingsValueMapWithStorage,
    mapStorageToConfig,
    saveSettingsToStorage,
    loadSettingsFromStorage,
    clearSettingsFromStorage,
    getVisibleTabs,
    getTabSettings,
    getTabLabels,
    getTabLabel,
    getPageConfig,
    getPageRoutes,
    hasPageRoute,
    getPageMetadata,
    getPageAgentLabel,
    getPageInfo
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

export const useConfigValue = (path: string, defaultValue?: unknown): unknown => {
  const { config } = useConfig();
  
  const getValue = (obj: unknown, path: string): unknown => {
    return path.split('.').reduce((current, key) => {
      if (current && typeof current === 'object' && key in current) {
        return (current as Record<string, unknown>)[key];
      }
      return undefined;
    }, obj);
  };

  const value = getValue(config, path);
  return value !== undefined ? value : defaultValue;
};

export const useFeatureFlag = (path: string): boolean => {
  const { config } = useConfig();
  
  const getValue = (obj: unknown, path: string): unknown => {
    return path.split('.').reduce((current, key) => {
      if (current && typeof current === 'object' && key in current) {
        return (current as Record<string, unknown>)[key];
      }
      return undefined;
    }, obj);
  };

  const value = getValue(config, path);
  return Boolean(value);
};

export const useAppConfig = () => {
  const { getAppConfig } = useConfig();
  return getAppConfig();
};

export const useStaticConfig = () => {
  const { getStaticConfig } = useConfig();
  return getStaticConfig();
};

export const useDynamicConfig = () => {
  const { getDynamicConfig } = useConfig();
  return getDynamicConfig();
}; 